<!--
 ZTree Component
 *
 * @since: 2021-04-27
 * @author: mk.ko
-->

<template>
  <div>
    <ul :class="['ztree', { 'ztree-disabled': this.isReadOnly }]"
        :id="ztreeId"
        :style="contentCss"></ul>

    <!--Context-Menu Slot-->
    <slot name="context-menu"/>
  </div>
</template>

<script>
  import '@secui/ztree/js/jquery.ztree.all'
  // exhide pack
  import '@secui/ztree/js/jquery.ztree.exhide'

  export default {
    props: {
      isReadOnly: {
        type: Boolean,
        default: false
      },

      setting: {
        type: Object,
        require: false,
        default: function () {
          return {}
        }
      },
      nodes: {
        type: Array,
        require: true,
        default: function () {
          return []
        }
      },
      contentCss: {
        type: Object,
        default () {
          return {}
        }
      },
      enableCallback: {
        type: Boolean,
        default: () => false
      },
      autoRender: {
        type: Boolean,
        default: true
      }
    },
    data () {
      return {
        ztreeId: void 0,
        ztreeObj: null,
        list: []
      }
    },
    computed: {
      ztreeSetting () {
        return {
          view: {
            showIcon: false // default to hide icon
          },
          callback: {
            // 사용자 정의 callback before 이벤트 매핑 (disable)
            beforeAsync: (...arg) => { // treeId<String>, treeNode<JSON>
              if (typeof _.get(this.setting, ['callback', 'beforeAsync']) === 'function') {
                return !this.checkDisable(...arg) && this.setting.callback.beforeAsync(...arg)
              }
              return !this.checkDisable(...arg)
            },
            beforeClick: (...arg) => { // treeId<String>, treeNode<JSON>, clickFlag<Number>
              const disable = this.checkDisable(...arg)
              if (disable) {
                return false
              }
              if (typeof _.get(this.setting, ['callback', 'beforeClick']) === 'function') {
                const nextStep = this.setting.callback.beforeClick(...arg)
                // TODO: Promise 타입일경우 beforeClick 체크 정상 동작되지 않음... 추후 개선(Tree.js 쪽 수정 필요할 듯)
                if (nextStep instanceof Promise) {
                  return nextStep.then(flag => {
                    return flag
                  })
                } else {
                  return nextStep
                }
              }
              return true
            },
            beforeDblClick: (...arg) => { // treeId<String>, treeNode<JSON>
              if (typeof _.get(this.setting, ['callback', 'beforeDblClick']) === 'function') {
                return !this.checkDisable(...arg) && this.setting.callback.beforeDblClick(...arg)
              }
              return !this.checkDisable(...arg)
            },
            beforeRightClick: (...arg) => { // treeId<String>, treeNode<JSON>
              if (typeof _.get(this.setting, ['callback', 'beforeRightClick']) === 'function') {
                return !this.checkDisable(...arg) && this.setting.callback.beforeRightClick(...arg)
              }
              return !this.checkDisable(...arg)
            },

            beforeCheck: (...arg) => { // treeId<String>, treeNode<JSON>
              if (typeof _.get(this.setting, ['callback', 'beforeCheck']) === 'function') {
                this.$emit('beforeCheck', ...arg)
                return !this.checkDisable(...arg) && this.setting.callback.beforeCheck(...arg) && !this.isReadOnly
              }
              return !this.checkDisable(...arg)
            },
            beforeDrag: (...arg) => { // treeId<String>, treeNodes<Array(JSON)>
              if (typeof _.get(this.setting, ['callback', 'beforeDrag']) === 'function') {
                return !this.checkDisable(...arg) && this.setting.callback.beforeDrag(...arg) && !this.isReadOnly
              }
              return !this.checkDisable(...arg)
            },
            beforeDragOpen: (...arg) => { // treeId<String>, treeNode<JSON>
              if (typeof _.get(this.setting, ['callback', 'beforeDragOpen']) === 'function') {
                return !this.checkDisable(...arg) && this.setting.callback.beforeDragOpen(...arg) && !this.isReadOnly
              }
              return !this.checkDisable(...arg)
            },
            beforeDrop: (...arg) => { // treeId<String>, treeNodes<Array(JSON)>, targetNode<JSON>, moveType<String>, isCopy<Boolean>
              if (typeof _.get(this.setting, ['callback', 'beforeDrop']) === 'function') {
                return !this.checkDisable(...arg) && this.setting.callback.beforeDrop(...arg) && !this.isReadOnly
              }
              return !this.checkDisable(...arg)
            },
            beforeEditName: (...arg) => { // treeId<String>, treeNode<JSON>
              if (typeof _.get(this.setting, ['callback', 'beforeEditName']) === 'function') {
                return !this.checkDisable(...arg) && this.setting.callback.beforeEditName(...arg) && !this.isReadOnly
              }
              return !this.checkDisable(...arg)
            },
            beforeRemove: (...arg) => { // treeId<String>, treeNode<JSON>
              if (typeof _.get(this.setting, ['callback', 'beforeRemove']) === 'function') {
                return !this.checkDisable(...arg) && this.setting.callback.beforeRemove(...arg) && !this.isReadOnly
              }
              return !this.checkDisable(...arg)
            },
            beforeRename: (...arg) => { // treeId<String>, treeNode<JSON>, newName<String>, isCancel<Boolean>
              if (typeof _.get(this.setting, ['callback', 'beforeRename']) === 'function') {
                return !this.checkDisable(...arg) && this.setting.callback.beforeRename(...arg) && !this.isReadOnly
              }
              return !this.checkDisable(...arg)
            },

            // disable 영향받지 않는 before 이벤트
            beforeCollapse: (...arg) => { // treeId<String>, treeNode<JSON>
              if (typeof _.get(this.setting, ['callback', 'beforeCollapse']) === 'function') {
                return this.setting.callback.beforeCollapse(...arg)
              }
            },
            beforeExpand: (...arg) => { // treeId<String>, treeNode<JSON>
              if (typeof _.get(this.setting, ['callback', 'beforeExpand']) === 'function') {
                return this.setting.callback.beforeExpand(...arg)
              }
            },
            beforeMouseDown: (...arg) => { // treeId<String>, treeNode<JSON>
              if (typeof _.get(this.setting, ['callback', 'beforeMouseDown']) === 'function') {
                return this.setting.callback.beforeMouseDown(...arg)
              }
            },
            beforeMouseUp: (...arg) => { // treeId<String>, treeNode<JSON>
              if (typeof _.get(this.setting, ['callback', 'beforeMouseUp']) === 'function') {
                return this.setting.callback.beforeMouseUp(...arg)
              }
            },

            onAsyncError: (...arg) => { // event, treeId<String>, treeNode<JSON>, XMLHttpRequest<String>, textStatus<String>, errorThrown<String>
              this.$emit('onAsyncError', ...arg)
            },
            onAsyncSuccess: (...arg) => { // event, treeId<String>, treeNode<JSON>, msg<String,Object>
              this.$emit('onAsyncSuccess', ...arg)
            },
            onCheck: (...arg) => { // event, treeId<String>, treeNode<JSON>
              this.$emit('onCheck', ...arg)
            },
            onClick: (...arg) => { // event, treeId<String>, treeNode<JSON>, clickFlag<Number>
              this.$emit('onClick', ...arg)
            },
            onCollapse: (...arg) => { // event, treeId<String>, treeNode<JSON>
              this.$emit('onCollapse', ...arg)
            },
            onDblClick: (...arg) => { // event, treeId<String>, treeNode<JSON>
              this.$emit('onDblClick', ...arg)
            },
            onDrag: (...arg) => { // event, treeId<String>, treeNode<Array(JSON)>
              this.$emit('onDrag', ...arg)
            },
            onDragMove: (...arg) => { // event, treeId<String>, treeNode<Array(JSON)>
              this.$emit('onDragMove', ...arg)
            },
            onDrop: (...arg) => { // event, treeId<String>, treeNode<Array(JSON)>, targetNode<JSON>, moveType<String>, isCopy<Boolean>
              this.$emit('onDrop', ...arg)
            },
            onExpand: (...arg) => { // event, treeId<String>, treeNode<JSON>
              this.$emit('onExpand', ...arg)
            },
            onMouseDown: (...arg) => { // event, treeId<String>, treeNode<JSON>
              this.$emit('onMouseDown', ...arg)
            },
            onMouseUp: (...arg) => { // event, treeId<String>, treeNode<JSON>
              this.$emit('onMouseUp', ...arg)
            },
            onNodeCreated: (...arg) => { // event, treeId<String>, treeNode<JSON>
              this.$emit('onNodeCreated', ...arg)
            },
            onRemove: (...arg) => { // event, treeId<String>, treeNode<JSON>
              this.$emit('onRemove', ...arg)
            },
            onRename: (...arg) => { // event, treeId<String>, treeNode<JSON>, isCancel(Boolean)
              this.$emit('onRename', ...arg)
            },
            onRightClick: (...arg) => { // event, treeId<String>, treeNode<JSON>
              this.$emit('onRightClick', ...arg)
            }
          }
        }
      }
    },
    mounted () {
      this.ztreeId = 'ztree_' + parseInt(Math.random() * 1e10)
    },
    beforeDestroy () {
      this.list = []
      this.ztreeId = void 0
      if (this.ztreeObj) {
        this.ztreeObj.destroy()
        this.ztreeObj = null
      }
    },
    methods: {
      setList (nodes = []) {
        try {
          this.list = nodes
        } catch (e) {
          this.$log('[zTree GUI Error (setList)]', e)
        }
      },
      checkDisable (treeId, treeNode) {
        try {
          const disable = _.get(treeNode, ['disable'], false)
          return disable !== null ? disable : false
        } catch (e) {
          this.$log('[zTree GUI Error (checkDisable)]', e)
        }
        return false
      },
      addNodes (...arg) {
        try {
          return this.ztreeObj.addNodes(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (addNodes)]', e)
        }
      },
      cancelEditName (...arg) {
        try {
          return this.ztreeObj.cancelEditName(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (cancelEditName)]', e)
        }
      },
      cancelSelectedNode (...arg) {
        try {
          return this.ztreeObj.cancelSelectedNode(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (cancelSelectedNode)]', e)
        }
      },
      checkAllNodes (...arg) {
        try {
          return this.ztreeObj.checkAllNodes(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (checkAllNodes)]', e)
        }
      },
      checkNode (...arg) {
        try {
          return this.ztreeObj.checkNode(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (checkNode)]', e)
        }
      },
      copyNode (...arg) {
        try {
          return this.ztreeObj.copyNode(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (copyNode)]', e)
        }
      },
      destroy (...arg) {
        try {
          return this.ztreeObj.destroy(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (destroy)]', e)
        }
      },
      editName (...arg) {
        try {
          return this.ztreeObj.editName(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (editName)]', e)
        }
      },
      expandAll (...arg) {
        try {
          return this.ztreeObj.expandAll(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (expandAll)]', e)
        }
      },
      expandNode (...arg) {
        try {
          return this.ztreeObj.expandNode(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (expandNode)]', e)
        }
      },
      getChangeCheckedNodes (...arg) {
        try {
          return this.ztreeObj.getChangeCheckedNodes(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (getChangeCheckedNodes)]', e)
        }
      },
      getCheckedNodes (...arg) {
        try {
          return this.ztreeObj.getCheckedNodes(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (getCheckedNodes)]', e)
        }
      },
      getNodeByParam (...arg) {
        try {
          return this.ztreeObj.getNodeByParam(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (getNodeByParam)]', e)
        }
      },
      getNodeByTId (...arg) {
        try {
          return this.ztreeObj.getNodeByTId(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (getNodeByTId)]', e)
        }
      },
      getNodeIndex (...arg) {
        try {
          return this.ztreeObj.getNodeIndex(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (getNodeIndex)]', e)
        }
      },
      getNodes (...arg) {
        try {
          return this.ztreeObj.getNodes(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (getNodes)]', e)
        }
      },
      getNodesByFilter (...arg) {
        try {
          return this.ztreeObj.getNodesByFilter(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (getNodesByFilter)]', e)
        }
      },
      getNodesByParam (...arg) {
        try {
          return this.ztreeObj.getNodesByParam(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (getNodesByParam)]', e)
        }
      },
      getNodesByParamFuzzy (...arg) {
        try {
          return this.ztreeObj.getNodesByParamFuzzy(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (getNodesByParamFuzzy)]', e)
        }
      },
      getSelectedNodes (...arg) {
        try {
          return this.ztreeObj.getSelectedNodes(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (getSelectedNodes)]', e)
        }
      },
      hideNode (...arg) {
        try {
          return this.ztreeObj.hideNode(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (hideNode)]', e)
        }
      },
      hideNodes (...arg) {
        try {
          return this.ztreeObj.hideNodes(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (hideNodes)]', e)
        }
      },
      moveNode (...arg) {
        try {
          return this.ztreeObj.moveNode(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (moveNode)]', e)
        }
      },
      reAsyncChildNodes (...arg) {
        try {
          return this.ztreeObj.reAsyncChildNodes(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (reAsyncChildNodes)]', e)
        }
      },
      reAsyncChildNodesPromise (...arg) {
        try {
          return this.ztreeObj.reAsyncChildNodesPromise(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (reAsyncChildNodesPromise)]', e)
        }
      },
      refresh (...arg) {
        try {
          return this.ztreeObj.refresh(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (refresh)]', e)
        }
      },
      removeChildNodes (...arg) {
        try {
          return this.ztreeObj.removeChildNodes(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (removeChildNodes)]', e)
        }
      },
      removeNode (...arg) {
        try {
          return this.ztreeObj.removeNode(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (removeNode)]', e)
        }
      },
      selectNode (...arg) {
        try {
          const treeNode = arg[0]
          if (!this.checkDisable(void 0, treeNode)) {
            return this.ztreeObj.selectNode(...arg)
          }
        } catch (e) {
          this.$log('[zTree GUI Error (selectNode)]', e)
        }
      },
      setChkDisabled (...arg) {
        try {
          return this.ztreeObj.setChkDisabled(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (setChkDisabled)]', e)
        }
      },
      setEditable (...arg) {
        try {
          return this.ztreeObj.setEditable(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (setEditable)]', e)
        }
      },
      showNode (...arg) {
        try {
          return this.ztreeObj.showNode(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (showNode)]', e)
        }
      },
      showNodes (...arg) {
        try {
          return this.ztreeObj.showNodes(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (showNodes)]', e)
        }
      },
      transformToArray (...arg) {
        try {
          return this.ztreeObj.transformToArray(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (transformToArray)]', e)
        }
      },
      transformTozTreeNodes (...arg) {
        try {
          return this.ztreeObj.transformTozTreeNodes(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (transformTozTreeNodes)]', e)
        }
      },
      updateNode (...arg) {
        try {
          return this.ztreeObj.updateNode(...arg)
        } catch (e) {
          this.$log('[zTree GUI Error (updateNode)]', e)
        }
      },

      collapseAll () {
        try {
          this.expandAll(false)
        } catch (e) {
          this.$log('[zTree GUI Error (expandAll)]', e)
        }
      },
      // _expandNode (type) {
      //   try {
      //     if (!type) { return }
      //     // var zTree = $.fn.zTree.getZTreeObj("treeDemo"),
      //     const nodes = this.ztreeObj.getSelectedNodes()
      //
      //     if (type.indexOf('All') < 0 && nodes.length === 0) {
      //       // 'Please select one parent node at first...'
      //       return
      //     }
      //
      //     if (type === 'expandAll') {
      //       this.ztreeObj.expandAll(true)
      //     } else if (type === 'collapseAll') {
      //       this.ztreeObj.expandAll(false)
      //     } else {
      //       const callbackFlag = this.enableCallback
      //       for (var i = 0, l = nodes.length; i < l; i++) {
      //         this.ztreeObj.setting.view.fontCss = {}
      //         if (type === 'expand') {
      //           this.ztreeObj.expandNode(nodes[i], true, null, null, callbackFlag)
      //         } else if (type === 'collapse') {
      //           this.ztreeObj.expandNode(nodes[i], false, null, null, callbackFlag)
      //         } else if (type === 'toggle') {
      //           this.ztreeObj.expandNode(nodes[i], null, null, null, callbackFlag)
      //         } else if (type === 'expandSon') {
      //           this.ztreeObj.expandNode(nodes[i], true, true, null, callbackFlag)
      //         } else if (type === 'collapseSon') {
      //           this.ztreeObj.expandNode(nodes[i], false, true, null, callbackFlag)
      //         }
      //       }
      //     }
      //   } catch (e) {
      //     this.$log('[zTree GUI Error (expandNode)]', e)
      //   }
      // },
      // _checkNode (type, node = []) {
      //   try {
      //     if (!type) { return }
      //     // var zTree = $.fn.zTree.getZTreeObj("treeDemo"),
      //     let nodes = node
      //     if (_.get(node, ['length'], 0) > 0) {
      //       nodes = this.ztreeObj.getSelectedNodes()
      //     }
      //
      //     if (type.indexOf('All') < 0 && nodes.length === 0) {
      //       // alert('Please select one node at first...')
      //       return
      //     }
      //
      //     if (type === 'checkAllTrue') {
      //       this.ztreeObj.checkAllNodes(true)
      //     } else if (type === 'checkAllFalse') {
      //       this.ztreeObj.checkAllNodes(false)
      //     } else {
      //       const callbackFlag = this.enableCallback
      //       for (let i = 0, l = nodes.length; i < l; i++) {
      //         if (type === 'checkTrue') {
      //           this.ztreeObj.checkNode(nodes[i], true, false, callbackFlag)
      //         } else if (type === 'checkFalse') {
      //           this.ztreeObj.checkNode(nodes[i], false, false, callbackFlag)
      //         } else if (type === 'toggle') {
      //           this.ztreeObj.checkNode(nodes[i], null, false, callbackFlag)
      //         } else if (type === 'checkTruePS') {
      //           this.ztreeObj.checkNode(nodes[i], true, true, callbackFlag)
      //         } else if (type === 'checkFalsePS') {
      //           this.ztreeObj.checkNode(nodes[i], false, true, callbackFlag)
      //         } else if (type === 'togglePS') {
      //           this.ztreeObj.snv up
      //           (nodes[i], null, true, callbackFlag)
      //         }
      //       }
      //     }
      //   } catch (e) {
      //     this.$log('[zTree GUI Error (checkNode)]', e)
      //   }
      // }
      render (nodes) {
        try {
          // update tree
          if (this.ztreeObj) {
            this.ztreeObj.destroy()
          }

          this.$nextTick(() => {
            const pickSettings = _.pick(this.setting, ['async', 'check', 'data', 'edit', 'view']) // callback 제외
            const settings = _.merge(this.ztreeSetting, pickSettings)
            let list = []
            if (typeof nodes !== 'undefined') {
              list = (_.get(nodes, ['length'], 0) > 0) ? nodes : []
            } else {
              list = this.list
            }

            this.ztreeObj = $.fn.zTree.init(
              $('#' + this.ztreeId),
              settings,
              list
            )

            this.$nextTick(() => {
              this.$emit('onCreated', this.ztreeObj)
            })
          })
        } catch (e) {
          this.$log('[zTree GUI Error (render)]', e)
        }
      }
    },
    watch: {
      nodes: {
        handler (nodes) {
          if (!this.autoRender) { return }

          try {
            this.list = nodes
            this.render()
          } catch (e) {
            this.$log('[zTree GUI Error (node handler watcher)]', e)
          }
        },
        deep: true,
        immediate: true
      }
    }
  }
</script>

<style scoped>
</style>
